import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";
import { ProgressBox } from "@/components/ProgressBox";
import { Link } from "react-router-dom";
import { CreditCard, ShieldCheck, Gift, KeyRound, Mail, ArrowRight, Copy, Map, Calculator, Bookmark, Receipt, Bell, User, FolderOpen } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
};
const REASONS = ["Not a fit for me", "Too expensive", "Technical issues", "Other"];
const inputCls = "w-full bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2.5 text-sm text-white focus:border-[#FD0F10] outline-none";

export default function Account() {
  const { user, refresh } = useAuth();
  const [billing, setBilling] = useState(null);
  const [prefs, setPrefs] = useState({ email_product: true, email_reminders: true });
  const [showRefund, setShowRefund] = useState(false);
  const [reason, setReason] = useState(REASONS[0]);
  const [comment, setComment] = useState("");
  const [cur, setCur] = useState(""); const [nw, setNw] = useState("");
  const [newEmail, setNewEmail] = useState(""); const [emailPw, setEmailPw] = useState("");
  const [name, setName] = useState(user?.name || ""); const [address, setAddress] = useState(["Mr.", "Miss", "Mrs."].includes(user?.pronouns) ? user.pronouns : "first_name");
  const [busy, setBusy] = useState("");

  const loadBilling = () => api.get("/account/billing").then(r => setBilling(r.data)).catch(() => {});
  useEffect(() => {
    loadBilling();
    api.get("/account/preferences").then(r => setPrefs(r.data)).catch(() => {});
  }, []);

  const openPortal = async (label) => {
    setBusy(label);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open billing portal"); setBusy(""); }
  };
  const submitRefund = async () => {
    setBusy("refund");
    try {
      await api.post("/refunds/request", { reason, comment });
      toast.success("Refund request sent. We'll be in touch.");
      setShowRefund(false); setComment(""); loadBilling();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send request"); }
    setBusy("");
  };
  const changePassword = async (e) => {
    e.preventDefault(); setBusy("pw");
    try { await api.post("/account/change-password", { current_password: cur, new_password: nw }); toast.success("Password updated"); setCur(""); setNw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update password"); }
    setBusy("");
  };
  const changeEmail = async (e) => {
    e.preventDefault(); setBusy("email");
    try {
      await api.post("/account/change-email", { new_email: newEmail, password: emailPw });
      toast.success("Login email updated");
      if (refresh) refresh();
      setNewEmail(""); setEmailPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update email"); }
    setBusy("");
  };
  const saveProfile = async (e) => {
    e.preventDefault(); setBusy("profile");
    try {
      await api.post("/account/profile", { name, pronouns: address });
      toast.success("Profile saved");
      if (refresh) refresh();
    } catch (e) { toast.error("Couldn't save profile"); }
    setBusy("");
  };
  const savePrefs = async (next) => {
    setPrefs(next);
    try { await api.post("/account/preferences", next); toast.success("Preferences saved"); }
    catch (e) { toast.error("Couldn't save preferences"); loadBilling(); }
  };
  const copyCode = () => { if (user?.referral_code) { navigator.clipboard?.writeText(user.referral_code); toast.success("Referral code copied"); } };

  const Toggle = ({ on, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`w-11 h-6 rounded-full transition-colors relative ${on ? "bg-[#FD0F10]" : "bg-[#2A2E39]"}`}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );

  return (
    <div className="max-w-2xl mx-auto px-5 pt-6 pb-10" data-testid="account-page">
      <div className="eyebrow text-[#FD0F10]">YOUR ACCOUNT</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">Home base</h1>
      <p className="text-gray-300 text-sm mt-2">Your settings, billing and referrals, all in one place.</p>

      {/* Small progress summary - links out, no duplication */}
      <div className="mt-5 grid grid-cols-4 gap-3" data-testid="account-progress-summary">
        <Link to="/roadmap" className="card-panel p-3 text-center hover:border-[#FD0F10]" data-testid="summary-path"><Map size={18} className="mx-auto text-[#FD0F10]" /><div className="text-[11px] text-gray-300 mt-1">Your path</div></Link>
        <Link to="/exit" className="card-panel p-3 text-center hover:border-[#FD0F10]" data-testid="summary-exit"><Calculator size={18} className="mx-auto text-[#FD0F10]" /><div className="text-[11px] text-gray-300 mt-1">Exit number</div></Link>
        <Link to="/saved" className="card-panel p-3 text-center hover:border-[#FD0F10]" data-testid="summary-saved"><Bookmark size={18} className="mx-auto text-[#FD0F10]" /><div className="text-[11px] text-gray-300 mt-1">Saved</div></Link>
        <Link to="/work" className="card-panel p-3 text-center hover:border-[#FD0F10]" data-testid="summary-my-work"><FolderOpen size={18} className="mx-auto text-[#FD0F10]" /><div className="text-[11px] text-gray-300 mt-1">My Work</div></Link>
      </div>

      <ProgressBox />

      {/* Subscription */}
      <section className="mt-5 card-panel p-5" data-testid="account-subscription">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><CreditCard size={14} /> SUBSCRIPTION</div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
            <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Plan</div>
            <div className="font-display font-black text-lg text-white mt-1" data-testid="account-plan">{billing?.plan || "—"}</div>
          </div>
          <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
            <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Price</div>
            <div className="font-display font-black text-lg text-white mt-1" data-testid="account-price">{billing?.price_label || "—"}</div>
          </div>
          <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-3">
            <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Renews</div>
            <div className="font-display font-black text-lg text-white mt-1" data-testid="account-next-billing">{fmtDate(billing?.next_billing_date)}</div>
          </div>
        </div>

        {billing?.is_premium ? (
          <>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => openPortal("portal")} disabled={busy === "portal"} className="inline-flex items-center gap-2 hero-accent text-white font-bold px-5 py-3 rounded disabled:opacity-60" data-testid="manage-billing-btn">{busy === "portal" ? "Opening…" : "Manage / Cancel subscription"} <ArrowRight size={16} /></button>
              <button onClick={() => openPortal("receipts")} disabled={busy === "receipts"} className="inline-flex items-center gap-2 border border-[#2A2E39] hover:border-white text-white font-bold px-5 py-3 rounded disabled:opacity-60" data-testid="receipts-btn"><Receipt size={16} /> Receipts</button>
            </div>
            <div className="mt-4 border-t border-[#2A2E39] pt-3 flex items-center justify-between">
              <div>
                <div className="font-mono-ui text-[9px] uppercase tracking-wider text-gray-400">Payment method</div>
                <div className="text-sm text-white mt-1" data-testid="account-card">{billing?.card ? `${billing.card.brand?.toUpperCase()} •••• ${billing.card.last4}` : "Managed in the billing portal"}</div>
              </div>
              <button onClick={() => openPortal("card")} disabled={busy === "card"} className="text-sm border border-[#2A2E39] hover:border-white text-white px-4 py-2 rounded disabled:opacity-60" data-testid="update-card-btn">Update card</button>
            </div>
          </>
        ) : (
          <Link to="/pro" className="mt-4 inline-flex items-center gap-2 hero-accent text-white font-bold px-5 py-3 rounded" data-testid="account-upgrade-btn">Go Pro <ArrowRight size={16} /></Link>
        )}
      </section>

      {/* Billing & Refunds */}
      <section className="mt-5 card-panel p-5" data-testid="account-billing">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><ShieldCheck size={14} /> BILLING & REFUNDS</div>
        <div className="mt-3 text-xs text-gray-400" data-testid="account-refund-policy">
          {billing?.refund_policy || "Not a fit? Get a full refund within 7 days of your first payment. Cancel anytime. Renewals are non-refundable."}
        </div>
        {billing?.refund_request_status && ["requested", "processing", "approved"].includes(billing.refund_request_status) ? (
          <div className="mt-3 text-sm text-gray-300" data-testid="account-refund-status">Refund request status: <span className="text-white font-bold capitalize">{billing.refund_request_status}</span></div>
        ) : billing?.refund_eligible ? (
          <div className="mt-3">
            {!showRefund ? (
              <button onClick={() => setShowRefund(true)} className="text-sm border border-[#FD0F10] text-[#FD0F10] px-4 py-2.5 rounded hover:bg-[#FD0F10] hover:text-white" data-testid="request-refund-btn">Request Refund</button>
            ) : (
              <div className="bg-[#0B0C0E] border border-[#2A2E39] rounded p-4" data-testid="refund-form">
                <label className="font-mono-ui text-[10px] uppercase tracking-wider text-gray-400">Reason</label>
                <select value={reason} onChange={(e) => setReason(e.target.value)} className={`${inputCls} mt-1`} data-testid="refund-reason">{REASONS.map(r => <option key={r} value={r}>{r}</option>)}</select>
                <label className="font-mono-ui text-[10px] uppercase tracking-wider text-gray-400 block mt-3">Comment (optional)</label>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className={`${inputCls} mt-1`} data-testid="refund-comment" />
                <div className="flex gap-2 mt-3">
                  <button onClick={submitRefund} disabled={busy === "refund"} className="hero-accent text-white font-bold px-4 py-2.5 rounded text-sm disabled:opacity-60" data-testid="refund-submit">{busy === "refund" ? "Sending…" : "Submit request"}</button>
                  <button onClick={() => setShowRefund(false)} className="border border-[#2A2E39] text-gray-300 px-4 py-2.5 rounded text-sm" data-testid="refund-cancel">Cancel</button>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </section>

      {/* Profile */}
      <section className="mt-5 card-panel p-5" data-testid="account-profile">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><User size={14} /> PROFILE</div>
        <form onSubmit={saveProfile} className="mt-3">
          <label className="font-mono-ui text-[10px] uppercase tracking-wider text-gray-400">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={`${inputCls} mt-1`} data-testid="profile-name" />
          <label className="font-mono-ui text-[10px] uppercase tracking-wider text-gray-400 block mt-3">How should your coach address you?</label>
          <select value={address} onChange={(e) => setAddress(e.target.value)} className={`${inputCls} mt-1`} data-testid="profile-address">
            <option value="Mr.">Mr.</option>
            <option value="Miss">Miss</option>
            <option value="Mrs.">Mrs.</option>
            <option value="first_name">Just use my first name</option>
          </select>
          <button type="submit" disabled={busy === "profile"} className="mt-3 border border-[#2A2E39] hover:border-white text-white font-bold px-4 py-2.5 rounded text-sm disabled:opacity-60" data-testid="profile-save">{busy === "profile" ? "Saving…" : "Save profile"}</button>
        </form>
      </section>

      {/* Account settings: email + password */}
      <section className="mt-5 card-panel p-5" data-testid="account-settings">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><KeyRound size={14} /> LOGIN & SECURITY</div>
        <div className="mt-3 text-sm text-gray-300">Signed in as <span className="text-white font-bold" data-testid="account-email">{user?.email}</span></div>
        <form onSubmit={changeEmail} className="mt-4 border-t border-[#2A2E39] pt-4">
          <div className="font-mono-ui text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-2"><Mail size={12} /> Update login email</div>
          <input type="email" required placeholder="New email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={`${inputCls} mt-2`} data-testid="change-email-input" />
          <input type="password" required placeholder="Current password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} className={`${inputCls} mt-2`} data-testid="change-email-password" />
          <button type="submit" disabled={busy === "email"} className="mt-3 border border-[#2A2E39] hover:border-white text-white font-bold px-4 py-2.5 rounded text-sm disabled:opacity-60" data-testid="change-email-submit">{busy === "email" ? "Saving…" : "Update email"}</button>
        </form>
        <form onSubmit={changePassword} className="mt-5 border-t border-[#2A2E39] pt-4">
          <div className="font-mono-ui text-[10px] uppercase tracking-wider text-gray-400 flex items-center gap-2"><KeyRound size={12} /> Change password</div>
          <input type="password" required placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className={`${inputCls} mt-2`} data-testid="change-password-current" />
          <input type="password" required placeholder="New password (8+ characters)" value={nw} onChange={(e) => setNw(e.target.value)} className={`${inputCls} mt-2`} data-testid="change-password-new" />
          <button type="submit" disabled={busy === "pw"} className="mt-3 border border-[#2A2E39] hover:border-white text-white font-bold px-4 py-2.5 rounded text-sm disabled:opacity-60" data-testid="change-password-submit">{busy === "pw" ? "Saving…" : "Update password"}</button>
        </form>
      </section>

      {/* Notification preferences */}
      <section className="mt-5 card-panel p-5" data-testid="account-preferences">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><Bell size={14} /> NOTIFICATIONS & EMAIL</div>
        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm text-gray-200">Product updates and tips</div>
          <Toggle on={prefs.email_product} onClick={() => savePrefs({ ...prefs, email_product: !prefs.email_product })} testid="pref-product" />
        </div>
        <div className="mt-3 flex items-center justify-between border-t border-[#2A2E39] pt-3">
          <div className="text-sm text-gray-200">Reminders and weekly check-ins</div>
          <Toggle on={prefs.email_reminders} onClick={() => savePrefs({ ...prefs, email_reminders: !prefs.email_reminders })} testid="pref-reminders" />
        </div>
      </section>

      {/* Referral */}
      <section className="mt-5 card-panel p-5" data-testid="account-referral">
        <div className="eyebrow text-[#FD0F10] flex items-center gap-2"><Gift size={14} /> YOUR REFERRAL CODE</div>
        <p className="text-sm text-gray-300 mt-2">Refer a friend who upgrades and you get 30 days of Pro free. Stackable.</p>
        <div className="mt-3 flex items-center gap-2">
          <code className="flex-1 bg-[#0B0C0E] border border-[#2A2E39] rounded px-3 py-2.5 text-sm text-white font-mono-ui tracking-wider" data-testid="referral-code">{user?.referral_code || "—"}</code>
          <button onClick={copyCode} className="border border-[#2A2E39] hover:border-white text-white px-3 py-2.5 rounded" data-testid="referral-copy" title="Copy"><Copy size={16} /></button>
        </div>
        <Link to="/referral" className="mt-3 inline-flex items-center gap-2 text-sm text-[#FD0F10] hover:underline" data-testid="referral-open">Open referral center <ArrowRight size={14} /></Link>
      </section>
    </div>
  );
}
